import { fetchUserAttributes, getCurrentUser } from '@aws-amplify/auth';
import { ADD_ON_NAMES } from '@chiroup/core/constants/chiroup-products';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import {
  Activity,
  Calculator,
  Computer,
  Crown,
  DollarSign,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  Network,
  Plus,
  Server,
  Share,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import addonRevenueMonthlyService from '../../services/addonRevenueMonthly.service';
import Header from '../layout/Header';
import DashboardNavigation from './components/DashboardNavigation';
import { SimpleValueLineChart } from './components/SimpleValueLineChart';
import StripeLoading from './components/StripeLoading';
import analyticsService from './services/analytics.service';

// Pure functions moved outside component to prevent recreation on every render
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;

  return num.toLocaleString();
};

const formatCurrency = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const StripeDashboard = () => {
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const navigate = useNavigate();

  // Stripe metrics are current snapshots, no date filtering needed
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'stripe'],
    () => analyticsService.getMetrics('stripe', { type: 'monthToDate' }), // Default filter for API compatibility
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes (longer since these are current snapshots)
      keepPreviousData: true,
    }
  );

  // Manual add-on revenue (must be declared before any conditional returns to follow Rules of Hooks)
  const ncmicSeriesQuery = useQuery(
    ['addon-revenue-monthly-series', 'ncmic'],
    () => addonRevenueMonthlyService.series('ncmic'),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const practisyncSeriesQuery = useQuery(
    ['addon-revenue-monthly-series', 'practisync'],
    () => addonRevenueMonthlyService.series('practisync'),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  // UI gating for the manage button (backend still enforces authorization)
  const { data: currentUserEmail } = useQuery(
    ['current-user-email'],
    async (): Promise<string | null> => {
      try {
        const attr = await fetchUserAttributes().catch(() => null as any);
        const currentUser = await getCurrentUser().catch(() => null as any);
        const email =
          (attr as any)?.email ||
          (currentUser as any)?.signInDetails?.loginId ||
          (currentUser as any)?.username ||
          null;
        return email ? String(email).toLowerCase() : null;
      } catch {
        return null;
      }
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );

  const canManageAddonRevenue = currentUserEmail === 'r.uppalapati@chiroup.com';

  // Prepare metrics data before any conditional returns to follow Rules of Hooks
  const allMetrics = data?.metrics || [];

  // Convert metrics array to object for direct access instead of O(n) find operations
  const metricsMap = useMemo(() => {
    const map: Record<string, number> = {};
    allMetrics.forEach(metric => {
      if (typeof metric?.value === 'number') {
        map[metric.id] = metric.value;
      }
    });

    return map;
  }, [allMetrics]);

  // Optimized metric value getter - O(1) lookup instead of O(n) find
  const getMetricValue = (id: string): number => {
    return metricsMap[id] ?? 0;
  };

  // Show full loading screen for initial load - only hide when both data is loaded AND minimum duration is complete
  const shouldShowInitialLoading = isLoading || !minimumDurationComplete;

  if (shouldShowInitialLoading) {
    return <StripeLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  // Show full loading screen for data refetching (without minimum duration)
  const shouldShowRefreshLoading = isFetching && !isLoading;

  if (shouldShowRefreshLoading) {
    return <StripeLoading />;
  }

  // Get Stripe metric values
  const basicPlanCount = getMetricValue('stripe-basic-plan-count');
  const basicPlanMRRBase = getMetricValue('stripe-basic-plan-mrr-base');
  const basicPlanMRRActual = getMetricValue('stripe-basic-plan-mrr-actual');
  const basicPlanDiscountImpact = getMetricValue('stripe-basic-plan-discount-impact');
  const plusPlanCount = getMetricValue('stripe-plus-plan-count');
  const plusPlanMRRBase = getMetricValue('stripe-plus-plan-mrr-base');
  const plusPlanMRRActual = getMetricValue('stripe-plus-plan-mrr-actual');
  const plusPlanDiscountImpact = getMetricValue('stripe-plus-plan-discount-impact');
  const premiumPlanCount = getMetricValue('stripe-premium-plan-count');
  const premiumPlanMRRBase = getMetricValue('stripe-premium-plan-mrr-base');
  const premiumPlanMRRActual = getMetricValue('stripe-premium-plan-mrr-actual');
  const premiumPlanDiscountImpact = getMetricValue('stripe-premium-plan-discount-impact');
  const ehrPlanCount = getMetricValue('stripe-ehr-plan-count');
  const ehrPlanMRRBase = getMetricValue('stripe-ehr-plan-mrr-base');
  const ehrPlanMRRActual = getMetricValue('stripe-ehr-plan-mrr-actual');
  const ehrPlanDiscountImpact = getMetricValue('stripe-ehr-plan-discount-impact');
  const locationsPlanCount = getMetricValue('stripe-locations-plan-count');
  const locationsPlanMRRBase = getMetricValue('stripe-locations-plan-mrr-base');
  const locationsPlanMRRActual = getMetricValue('stripe-locations-plan-mrr-actual');
  const locationsPlanDiscountImpact = getMetricValue('stripe-locations-plan-discount-impact');
  const additionalProvidersPlanCount = getMetricValue('stripe-additional-providers-plan-count');
  const additionalProvidersPlanMRRBase = getMetricValue(
    'stripe-additional-providers-plan-mrr-base'
  );
  const additionalProvidersPlanMRRActual = getMetricValue(
    'stripe-additional-providers-plan-mrr-actual'
  );
  const additionalProvidersPlanDiscountImpact = getMetricValue(
    'stripe-additional-providers-plan-discount-impact'
  );
  const socialMediaAddonPlanCount = getMetricValue('stripe-social-media-addon-plan-count');
  const socialMediaAddonPlanMRRBase = getMetricValue('stripe-social-media-addon-plan-mrr-base');
  const newslettersPlanCount = getMetricValue('stripe-newsletters-plan-count');
  const newslettersPlanMRRBase = getMetricValue('stripe-newsletters-plan-mrr-base');
  const x12LicensureFeePlanCount = getMetricValue('stripe-x12-licensure-fee-plan-count');
  const x12LicensureFeePlanMRRBase = getMetricValue('stripe-x12-licensure-fee-plan-mrr-base');
  const infinediIntegrationCount = getMetricValue('stripe-infinedi-integration-count');
  const infinediIntegrationMRRBase = getMetricValue('stripe-infinedi-integration-mrr-base');
  const trizettoIntegrationCount = getMetricValue('stripe-trizetto-integration-count');
  const trizettoIntegrationMRRBase = getMetricValue('stripe-trizetto-integration-mrr-base');
  const voiceToChartPlanCount = getMetricValue('stripe-voice-to-chart-plan-count');
  const voiceToChartPlanMRRBase = getMetricValue('stripe-voice-to-chart-plan-mrr-base');
  const advancedSMSPlanCount = getMetricValue('stripe-advanced-sms-plan-count');
  const advancedSMSPlanMRRBase = getMetricValue('stripe-advanced-sms-plan-mrr-base');
  const totalMRR = getMetricValue('stripe-total-mrr');
  const totalActiveSubscriptions = getMetricValue('stripe-total-active-subscriptions');

  const toNumber = (v: any): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const ncmicLatest = toNumber(ncmicSeriesQuery.data?.series?.slice(-1)?.[0]?.value ?? 0);
  const practisyncLatest = toNumber(practisyncSeriesQuery.data?.series?.slice(-1)?.[0]?.value ?? 0);
  const manualAddonRevenue = ncmicLatest + practisyncLatest;

  // Calculate Add-ons totals
  const totalAddonSubscriptions =
    socialMediaAddonPlanCount +
    newslettersPlanCount +
    x12LicensureFeePlanCount +
    infinediIntegrationCount +
    trizettoIntegrationCount +
    voiceToChartPlanCount +
    advancedSMSPlanCount;

  const totalAddonMRR =
    socialMediaAddonPlanMRRBase +
    newslettersPlanMRRBase +
    x12LicensureFeePlanMRRBase +
    infinediIntegrationMRRBase +
    trizettoIntegrationMRRBase +
    voiceToChartPlanMRRBase +
    advancedSMSPlanMRRBase;

  // Option A: UI-only aggregation of manual revenue into add-on totals + total MRR
  const totalAddonMRRWithManual = totalAddonMRR + manualAddonRevenue;
  const totalMRRWithManual = totalMRR + manualAddonRevenue;

  // Calculate Subscriptions totals
  const totalSubscriptionCount =
    basicPlanCount +
    plusPlanCount +
    premiumPlanCount +
    ehrPlanCount +
    locationsPlanCount +
    additionalProvidersPlanCount;

  const totalSubscriptionBaseMRR =
    basicPlanMRRBase +
    plusPlanMRRBase +
    premiumPlanMRRBase +
    ehrPlanMRRBase +
    locationsPlanMRRBase +
    additionalProvidersPlanMRRBase;

  const totalSubscriptionActualMRR =
    basicPlanMRRActual +
    plusPlanMRRActual +
    premiumPlanMRRActual +
    ehrPlanMRRActual +
    locationsPlanMRRActual +
    additionalProvidersPlanMRRActual;

  const totalSubscriptionDiscountImpact =
    basicPlanDiscountImpact +
    plusPlanDiscountImpact +
    premiumPlanDiscountImpact +
    ehrPlanDiscountImpact +
    locationsPlanDiscountImpact +
    additionalProvidersPlanDiscountImpact;

  // Helper function to calculate percentage
  const calculatePercentage = (value: number, total: number): string => {
    if (!Number.isFinite(total) || total <= 0) return '0.0%';
    if (!Number.isFinite(value)) return '0.0%';

    return `${((value / total) * 100).toFixed(1)}%`;
  };

  // Helper function to calculate ARPU (Average Revenue Per User)
  const calculateARPU = (mrr: number, subscribers: number): number => {
    if (subscribers === 0) return 0;

    return mrr / subscribers;
  };

  // Helper function to calculate MRR percentage of total revenue
  const calculateMRRPercentage = (planMRR: number, totalMRR: number): string => {
    if (totalMRR === 0) return '0.0%';

    return `${((planMRR / totalMRR) * 100).toFixed(1)}%`;
  };

  // Helper function to format MRR with percentage
  const formatMRRWithPercentage = (planMRR: number, totalMRR: number): string => {
    const percentage = calculateMRRPercentage(planMRR, totalMRR);

    return `${formatCurrency(planMRR)} (${percentage})`;
  };

  // Helper function to format price incentives with incentive rate
  const formatDiscountWithRate = (discountImpact: number, baseMRR: number): string => {
    if (baseMRR === 0) return `${formatCurrency(discountImpact)} (0.0%)`;
    const discountRate = ((discountImpact / baseMRR) * 100).toFixed(1);

    return `${formatCurrency(discountImpact)} (${discountRate}%)`;
  };

  // Helper function to format add-on MRR with percentage of total add-ons
  const formatAddonMRRWithPercentage = (addonMRR: number, totalAddonMRR: number): string => {
    const percentage = calculatePercentage(addonMRR, totalAddonMRR);

    return `${formatCurrency(addonMRR)} (${percentage})`;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-red-500 text-lg font-medium mb-2">
              Error loading Stripe metrics
            </div>
            <div className="text-gray-600">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <DashboardNavigation currentDashboardId="stripe" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Executive Overview - Hero Section */}
        <motion.div
          className="relative overflow-hidden rounded-3xl p-8 text-white shadow-2xl mb-8 bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 border-4 border-emerald-500/20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          {/* Animated background patterns */}
          <div className="absolute inset-0 opacity-10">
            <motion.div
              className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl"
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.1, 0.15, 0.1],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
            />
          </div>

          {/* Header */}
          <div className="relative z-10 mb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div>
                <h1 className="text-4xl font-black text-white drop-shadow-lg mb-2">
                  Stripe Business Dashboard
                </h1>
                <p className="text-emerald-100/80 text-sm font-medium">
                  Note: Subscription counts may not match SF due to billing cycles
                </p>
              </div>

              {/* Live Data Indicator - Dynamic Compact */}
              <motion.div
                className="bg-white/8 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/15 shadow-lg hover:bg-white/12 hover:border-white/25 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center space-x-3">
                  {/* Dynamic Status Indicator */}
                  <div className="relative">
                    <motion.div
                      className="w-2.5 h-2.5 bg-emerald-400 rounded-full"
                      animate={{
                        scale: [1, 1.3, 1],
                        boxShadow: [
                          '0 0 0 rgba(16, 185, 129, 0)',
                          '0 0 8px rgba(16, 185, 129, 0.6)',
                          '0 0 0 rgba(16, 185, 129, 0)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Subtle ripple on hover */}
                    <motion.div
                      className="absolute inset-0 bg-emerald-400/30 rounded-full opacity-0 group-hover:opacity-100"
                      animate={{
                        scale: [1, 2],
                        opacity: [0, 0.3, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  </div>

                  {/* Dynamic Live Data Text */}
                  <div className="flex flex-col space-y-0.5">
                    <div className="flex items-center space-x-2">
                      <motion.span
                        className="text-white text-sm font-semibold"
                        animate={{
                          color: ['#ffffff', '#f0fdf4', '#ffffff'],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        Live Data
                      </motion.span>
                      <motion.span
                        className="text-emerald-300 text-xs font-medium bg-emerald-500/20 px-2 py-0.5 rounded-md border border-emerald-500/30"
                        animate={{
                          backgroundColor: [
                            'rgba(16, 185, 129, 0.2)',
                            'rgba(16, 185, 129, 0.3)',
                            'rgba(16, 185, 129, 0.2)',
                          ],
                          borderColor: [
                            'rgba(16, 185, 129, 0.3)',
                            'rgba(16, 185, 129, 0.5)',
                            'rgba(16, 185, 129, 0.3)',
                          ],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        Updates Every 3hrs
                      </motion.span>
                    </div>
                    <div className="flex items-center space-x-3 text-emerald-100/80 text-xs">
                      <motion.span
                        animate={{ opacity: [0.8, 1, 0.8] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: 0 }}
                      >
                        Last:{' '}
                        {(() => {
                          const now = new Date();
                          const lastUpdate = new Date(now);
                          lastUpdate.setHours(Math.floor(now.getHours() / 3) * 3, 0, 0, 0);

                          return lastUpdate.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          });
                        })()}
                      </motion.span>
                      <motion.span
                        className="text-emerald-200/70"
                        animate={{
                          opacity: [0.7, 1, 0.7],
                          color: [
                            'rgba(167, 243, 208, 0.7)',
                            'rgba(167, 243, 208, 1)',
                            'rgba(167, 243, 208, 0.7)',
                          ],
                        }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: 1 }}
                      >
                        • Next:{' '}
                        {(() => {
                          const now = new Date();
                          const nextUpdate = new Date(now);
                          nextUpdate.setHours(now.getHours() + 3 - (now.getHours() % 3), 0, 0, 0);

                          return nextUpdate.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          });
                        })()}
                      </motion.span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* KPI Cards Grid */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Subscription MRR */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-3">
                <DollarSign className="w-6 h-6 text-white drop-shadow-lg" />
                <TrendingUp className="w-4 h-4 text-green-300" />
              </div>
              <div className="text-3xl font-black mb-1 text-white drop-shadow-lg">
                {formatCurrency(totalSubscriptionActualMRR)}
              </div>
              <div className="text-xs font-semibold text-green-100 mb-1">Subscription MRR</div>
              <div className="text-xs text-green-200/80">
                <span className="font-bold text-white">
                  {totalSubscriptionCount.toLocaleString()}
                </span>{' '}
                Active Subscriptions
              </div>
            </motion.div>

            {/* Total Add-on MRR */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-3">
                <Plus className="w-6 h-6 text-emerald-300 drop-shadow-lg" />
                <span className="text-xs text-emerald-200 font-medium">Add-ons</span>
              </div>
              <div className="text-3xl font-black mb-1 text-white drop-shadow-lg">
                {formatCurrency(totalAddonMRRWithManual)}
              </div>
              <div className="text-xs font-semibold text-emerald-200">Add-on MRR</div>
              <div className="text-xs text-emerald-200/80">
                <span className="font-bold text-white">
                  {totalAddonSubscriptions.toLocaleString()}
                </span>{' '}
                Active Add-ons
              </div>
            </motion.div>

            {/* Total Active Subscriptions */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-3">
                <Users className="w-6 h-6 text-purple-300 drop-shadow-lg" />
                <span className="text-xs text-purple-200 font-medium">Active</span>
              </div>
              <div className="text-3xl font-black mb-1 text-white drop-shadow-lg">
                {(totalSubscriptionCount + totalAddonSubscriptions).toLocaleString()}
              </div>
              <div className="text-xs font-semibold text-purple-200">Total Subscriptions</div>
              <div className="text-xs text-purple-200/80">All Active Plans</div>
            </motion.div>

            {/* Total Revenue */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-3">
                <Activity className="w-6 h-6 text-yellow-300 drop-shadow-lg" />
              </div>
              <div className="text-3xl font-black mb-1 text-white drop-shadow-lg">
                {formatCurrency(totalMRRWithManual)}
              </div>
              <div className="text-xs font-semibold text-yellow-200">Total MRR</div>
              <div className="text-xs text-yellow-200/80">All Revenue Streams</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="grid w-full grid-cols-1 sm:grid-cols-3 gap-3 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl max-w-2xl mx-auto mb-8">
            <Tabs.Trigger
              value="subscriptions"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:via-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Users className="h-4 w-4" />
                <span>Subscriptions</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="addons"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-green-500 data-[state=active]:via-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus className="h-4 w-4" />
                <span>Add-ons</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="visualizations"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:via-violet-500 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <TrendingUp className="h-4 w-4" />
                <span>Visualisations</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>

          {/* Tab Content */}
          <Tabs.Content value="subscriptions" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Modern Table Container */}
              <div className="bg-gradient-to-br from-white via-white to-gray-50/50 rounded-2xl border border-gray-200/50 backdrop-blur-sm shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/10 via-transparent to-purple-50/10 opacity-50"></div>

                <div className="relative">
                  <table className="min-w-full">
                    <thead>
                      <motion.tr
                        className="bg-gradient-to-r from-slate-200/90 via-blue-200/70 to-indigo-200/70 border-b-3 border-gradient-to-r from-blue-300 to-indigo-300 backdrop-blur-md shadow-md"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      >
                        <motion.th
                          className="px-6 py-6 text-left text-xs font-bold text-slate-800 uppercase tracking-wider w-1/4 group cursor-pointer hover:bg-blue-100/60 transition-all duration-300 rounded-tl-xl"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center space-x-3">
                            <motion.div
                              className="w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-lg"
                              animate={{
                                scale: [1, 1.2, 1],
                                boxShadow: [
                                  '0 0 5px rgba(59, 130, 246, 0.5)',
                                  '0 0 15px rgba(59, 130, 246, 0.8)',
                                  '0 0 5px rgba(59, 130, 246, 0.5)',
                                ],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              }}
                            />
                            <span className="group-hover:text-blue-700 transition-colors duration-300">
                              Subscription Plan
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-800 uppercase tracking-wider w-1/8 group cursor-pointer hover:bg-blue-100/60 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <Users className="h-4 w-4 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-blue-700 transition-colors duration-300">
                              Active
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-800 uppercase tracking-wider w-1/8 group cursor-pointer hover:bg-green-100/60 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <DollarSign className="h-4 w-4 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-green-700 transition-colors duration-300">
                              MSRP MRR
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-1/6 group cursor-pointer hover:bg-purple-50/50 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <TrendingUp className="h-4 w-4 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-purple-700 transition-colors duration-300">
                              Actual MRR
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-6 py-6 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-1/8 group cursor-pointer hover:bg-orange-50/50 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <TrendingUp className="h-4 w-4 text-orange-600 group-hover:text-orange-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-orange-700 transition-colors duration-300">
                              Discounts
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-1/8 group cursor-pointer hover:bg-emerald-50/50 transition-all duration-300 rounded-tr-xl"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <Activity className="h-4 w-4 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-emerald-700 transition-colors duration-300">
                              ARPU
                            </span>
                          </div>
                        </motion.th>
                      </motion.tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100/40">
                      {/* Basic Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-blue-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(59, 130, 246, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.1,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Users className="h-5 w-5 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">
                                Basic Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-blue-800">
                          {formatNumber(basicPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(basicPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(basicPlanMRRActual, totalSubscriptionActualMRR)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(basicPlanDiscountImpact, basicPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(calculateARPU(basicPlanMRRActual, basicPlanCount))}
                        </td>
                      </motion.tr>

                      {/* Plus Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-purple-50/80 hover:to-pink-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-purple-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(147, 51, 234, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.2,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-purple-200 group-hover:to-purple-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(147, 51, 234, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Users className="h-5 w-5 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-purple-700 transition-colors">
                                Plus Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatNumber(plusPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(plusPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(plusPlanMRRActual, totalSubscriptionActualMRR)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(plusPlanDiscountImpact, plusPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(calculateARPU(plusPlanMRRActual, plusPlanCount))}
                        </td>
                      </motion.tr>

                      {/* Premium Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-amber-50/80 hover:to-yellow-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-amber-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(245, 158, 11, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.3,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-amber-200 group-hover:to-amber-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(245, 158, 11, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Crown className="h-5 w-5 text-amber-600 group-hover:text-amber-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-amber-700 transition-colors">
                                Premium Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-amber-800">
                          {formatNumber(premiumPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(premiumPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(
                            premiumPlanMRRActual,
                            totalSubscriptionActualMRR
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(premiumPlanDiscountImpact, premiumPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(calculateARPU(premiumPlanMRRActual, premiumPlanCount))}
                        </td>
                      </motion.tr>

                      {/* EHR Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-emerald-50/80 hover:to-teal-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.4,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-emerald-200 group-hover:to-emerald-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Computer className="h-5 w-5 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">
                                EHR Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatNumber(ehrPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(ehrPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(ehrPlanMRRActual, totalSubscriptionActualMRR)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(ehrPlanDiscountImpact, ehrPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(calculateARPU(ehrPlanMRRActual, ehrPlanCount))}
                        </td>
                      </motion.tr>

                      {/* Locations Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-sky-50/80 hover:to-cyan-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-sky-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(14, 165, 233, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.5,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-sky-100 to-sky-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-sky-200 group-hover:to-sky-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(14, 165, 233, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <MapPin className="h-5 w-5 text-sky-600 group-hover:text-sky-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-sky-700 transition-colors">
                                Locations Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-sky-800">
                          {formatNumber(locationsPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(locationsPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(
                            locationsPlanMRRActual,
                            totalSubscriptionActualMRR
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(
                            locationsPlanDiscountImpact,
                            locationsPlanMRRBase
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(
                            calculateARPU(locationsPlanMRRActual, locationsPlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Additional Providers Plan Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-violet-50/80 hover:to-purple-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-violet-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(139, 92, 246, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.6,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-violet-100 to-violet-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-violet-200 group-hover:to-violet-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(139, 92, 246, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Users className="h-5 w-5 text-violet-600 group-hover:text-violet-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-violet-700 transition-colors">
                                Additional Providers Plan
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-violet-800">
                          {formatNumber(additionalProvidersPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(additionalProvidersPlanMRRBase)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatMRRWithPercentage(
                            additionalProvidersPlanMRRActual,
                            totalSubscriptionActualMRR
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-800">
                          {formatDiscountWithRate(
                            additionalProvidersPlanDiscountImpact,
                            additionalProvidersPlanMRRBase
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatCurrency(
                            calculateARPU(
                              additionalProvidersPlanMRRActual,
                              additionalProvidersPlanCount
                            )
                          )}
                        </td>
                      </motion.tr>

                      {/* Total Subscriptions Row */}
                      <motion.tr
                        className="border-t-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 group hover:from-blue-200/90 hover:to-indigo-200/90 transition-all duration-500 hover:shadow-xl border-l border-r border-transparent hover:border-blue-300/80 rounded-lg hover:rounded-xl"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          transition: { type: 'spring', stiffness: 300, damping: 30 },
                        }}
                        transition={{ type: 'spring', stiffness: 200 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-xl mr-4 shadow-lg group-hover:shadow-xl transition-all duration-300"
                              whileHover={{
                                scale: 1.05,
                                transition: { type: 'spring', stiffness: 300, damping: 20 },
                              }}
                            >
                              <Calculator className="h-5 w-5 text-blue-700" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-blue-900 group-hover:text-blue-800 transition-colors">
                                Total Subscriptions
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-blue-900">
                          {totalSubscriptionCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-900">
                          {formatCurrency(totalSubscriptionBaseMRR)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-900">
                          {formatCurrency(totalSubscriptionActualMRR)} (100.0%)
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-bold text-orange-900">
                          {formatDiscountWithRate(
                            totalSubscriptionDiscountImpact,
                            totalSubscriptionBaseMRR
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-900">
                          {formatCurrency(
                            calculateARPU(totalSubscriptionActualMRR, totalSubscriptionCount)
                          )}
                        </td>
                      </motion.tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </Tabs.Content>

          <Tabs.Content value="addons" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Modern Table Container */}
              <div className="bg-gradient-to-br from-white via-white to-gray-50/50 rounded-2xl border border-gray-200/50 backdrop-blur-sm shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/10 via-transparent to-teal-50/10 opacity-50"></div>

                <div className="relative">
                  <table className="min-w-full">
                    <thead>
                      <motion.tr
                        className="bg-gradient-to-r from-slate-200/90 via-emerald-200/70 to-teal-200/70 border-b-3 border-gradient-to-r from-emerald-300 to-teal-300 backdrop-blur-md shadow-md"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      >
                        <motion.th
                          className="px-6 py-6 text-left text-xs font-bold text-slate-800 uppercase tracking-wider w-2/5 group cursor-pointer hover:bg-emerald-100/60 transition-all duration-300 rounded-tl-xl"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center space-x-3">
                            <motion.div
                              className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-lg"
                              animate={{
                                scale: [1, 1.2, 1],
                                boxShadow: [
                                  '0 0 5px rgba(16, 185, 129, 0.5)',
                                  '0 0 15px rgba(16, 185, 129, 0.8)',
                                  '0 0 5px rgba(16, 185, 129, 0.5)',
                                ],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              }}
                            />
                            <span className="group-hover:text-emerald-700 transition-colors duration-300">
                              Add-on Name
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-800 uppercase tracking-wider w-1/5 group cursor-pointer hover:bg-emerald-100/60 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <Users className="h-4 w-4 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-emerald-700 transition-colors duration-300">
                              Active
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-1/5 group cursor-pointer hover:bg-green-50/50 transition-all duration-300"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <DollarSign className="h-4 w-4 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-green-700 transition-colors duration-300">
                              MSRP MRR
                            </span>
                          </div>
                        </motion.th>
                        <motion.th
                          className="px-4 py-6 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-1/5 group cursor-pointer hover:bg-teal-50/50 transition-all duration-300 rounded-tr-xl"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4, duration: 0.5 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <motion.div
                              whileHover={{ rotate: 360, scale: 1.2 }}
                              transition={{ duration: 0.5 }}
                            >
                              <Activity className="h-4 w-4 text-teal-600 group-hover:text-teal-700 transition-colors duration-300" />
                            </motion.div>
                            <span className="group-hover:text-teal-700 transition-colors duration-300">
                              ARPU
                            </span>
                          </div>
                        </motion.th>
                      </motion.tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100/40">
                      {/* Social Media Add-on Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-pink-50/80 hover:to-rose-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-pink-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(236, 72, 153, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.1,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-pink-200 group-hover:to-pink-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(236, 72, 153, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Share className="h-5 w-5 text-pink-600 group-hover:text-pink-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-pink-700 transition-colors">
                                Social Media Add-on
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-pink-800">
                          {formatNumber(socialMediaAddonPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            socialMediaAddonPlanMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(socialMediaAddonPlanMRRBase, socialMediaAddonPlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Newsletters Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-cyan-50/80 hover:to-blue-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-cyan-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(6, 182, 212, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.2,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-cyan-200 group-hover:to-cyan-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(6, 182, 212, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Mail className="h-5 w-5 text-cyan-600 group-hover:text-cyan-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-cyan-700 transition-colors">
                                Newsletters
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-cyan-800">
                          {formatNumber(newslettersPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            newslettersPlanMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(newslettersPlanMRRBase, newslettersPlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* X12 Licensure Fee Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-amber-50/80 hover:to-yellow-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-amber-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(245, 158, 11, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.3,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-amber-200 group-hover:to-amber-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(245, 158, 11, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <FileText className="h-5 w-5 text-amber-600 group-hover:text-amber-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-amber-700 transition-colors">
                                X12 Licensure Fee
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-amber-800">
                          {formatNumber(x12LicensureFeePlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            x12LicensureFeePlanMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(x12LicensureFeePlanMRRBase, x12LicensureFeePlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* InfinEDI Integration Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-emerald-50/80 hover:to-green-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.4,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-emerald-200 group-hover:to-emerald-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Network className="h-5 w-5 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">
                                InfinEDI Integration
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-800">
                          {formatNumber(infinediIntegrationCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            infinediIntegrationMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(infinediIntegrationMRRBase, infinediIntegrationCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Trizetto Integration Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-blue-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(59, 130, 246, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.5,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Server className="h-5 w-5 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">
                                Trizetto Integration
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-blue-800">
                          {formatNumber(trizettoIntegrationCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            trizettoIntegrationMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(trizettoIntegrationMRRBase, trizettoIntegrationCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Voice to Chart Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-purple-50/80 hover:to-violet-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-purple-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(147, 51, 234, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.6,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-purple-200 group-hover:to-purple-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(147, 51, 234, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <Mic className="h-5 w-5 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-purple-700 transition-colors">
                                {ADD_ON_NAMES['voice-to-chart']}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-800">
                          {formatNumber(voiceToChartPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            voiceToChartPlanMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(voiceToChartPlanMRRBase, voiceToChartPlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Advanced SMS Row */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-sky-50/80 hover:to-blue-50/60 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-sky-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          boxShadow: '0 10px 25px rgba(14, 165, 233, 0.15)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.7,
                          duration: 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-sky-100 to-sky-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl group-hover:from-sky-200 group-hover:to-sky-300 transition-all duration-300"
                              whileHover={{
                                scale: 1.1,
                                rotate: 5,
                                boxShadow: '0 8px 20px rgba(14, 165, 233, 0.25)',
                                transition: { type: 'spring', stiffness: 400, damping: 20 },
                              }}
                            >
                              <MessageSquare className="h-5 w-5 text-sky-600 group-hover:text-sky-700 transition-colors duration-300" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-sky-700 transition-colors">
                                Advanced SMS
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-sky-800">
                          {formatNumber(advancedSMSPlanCount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatAddonMRRWithPercentage(
                            advancedSMSPlanMRRBase,
                            totalAddonMRRWithManual
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-800">
                          {formatCurrency(
                            calculateARPU(advancedSMSPlanMRRBase, advancedSMSPlanCount)
                          )}
                        </td>
                      </motion.tr>

                      {/* Manual Residual Income Rows (Monthly totals) */}
                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-emerald-50/60 hover:to-teal-50/40 transition-all duration-300 border border-transparent hover:border-emerald-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.004,
                          y: -1,
                          boxShadow: '0 8px 18px rgba(16, 185, 129, 0.10)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl transition-all duration-300">
                              <FileText className="h-5 w-5 text-emerald-700" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">
                                NCMIC
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-gray-500">
                          N/A
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(ncmicLatest)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-gray-500">
                          N/A
                        </td>
                      </motion.tr>

                      <motion.tr
                        className="group hover:bg-gradient-to-r hover:from-emerald-50/60 hover:to-teal-50/40 transition-all duration-300 border border-transparent hover:border-emerald-200/50 rounded-xl hover:rounded-2xl backdrop-blur-sm"
                        whileHover={{
                          scale: 1.004,
                          y: -1,
                          boxShadow: '0 8px 18px rgba(16, 185, 129, 0.10)',
                          transition: { type: 'spring', stiffness: 400, damping: 25 },
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl mr-4 shadow-sm group-hover:shadow-xl transition-all duration-300">
                              <Network className="h-5 w-5 text-emerald-700" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">
                                Practisync
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-gray-500">
                          N/A
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-800">
                          {formatCurrency(practisyncLatest)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-gray-500">
                          N/A
                        </td>
                      </motion.tr>

                      {/* Total Row */}
                      <motion.tr
                        className="border-t-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 group hover:from-emerald-200/90 hover:to-teal-200/90 transition-all duration-500 hover:shadow-xl border-l border-r border-transparent hover:border-emerald-300/80 rounded-lg hover:rounded-xl"
                        whileHover={{
                          scale: 1.008,
                          y: -2,
                          transition: { type: 'spring', stiffness: 300, damping: 30 },
                        }}
                        transition={{ type: 'spring', stiffness: 200 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <motion.div
                              className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-xl mr-4 shadow-lg group-hover:shadow-xl transition-all duration-300"
                              whileHover={{
                                scale: 1.05,
                                transition: { type: 'spring', stiffness: 300, damping: 20 },
                              }}
                            >
                              <Calculator className="h-5 w-5 text-emerald-700" />
                            </motion.div>
                            <div>
                              <span className="text-sm font-bold text-emerald-900 group-hover:text-emerald-800 transition-colors">
                                Total Add-ons
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-emerald-900">
                          {totalAddonSubscriptions.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-green-900">
                          {formatCurrency(totalAddonMRRWithManual)} (100.0%)
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-teal-900">
                          {formatCurrency(
                            calculateARPU(totalAddonMRRWithManual, totalAddonSubscriptions)
                          )}
                        </td>
                      </motion.tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </Tabs.Content>

          <Tabs.Content value="visualizations" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl border border-gray-200/50 shadow-lg p-8">
                <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                  <div>
                    <h2 className="text-xl font-black text-gray-900 mb-2">Visualisations</h2>
                    <p className="text-sm text-gray-600">
                      Manual residual income trends (monthly totals)
                    </p>
                  </div>
                  {canManageAddonRevenue ? (
                    <button
                      onClick={() => navigate('/analytics/stripe/addon-revenue')}
                      className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold shadow hover:bg-emerald-700 transition-colors"
                    >
                      Manage Add-on Revenue
                    </button>
                  ) : null}
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <SimpleValueLineChart
                    title="NCMIC Revenue Trend"
                    series={ncmicSeriesQuery.data?.series ?? []}
                    valueFormatter={formatCurrency}
                  />
                  <SimpleValueLineChart
                    title="Practisync Revenue Trend"
                    series={practisyncSeriesQuery.data?.series ?? []}
                    valueFormatter={formatCurrency}
                  />
                </div>
              </div>
            </motion.div>
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default StripeDashboard;
